import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const ActiveCampaignCtx = createContext(null);

const STORAGE_KEY = "harmony_active_campaign_id";

export function ActiveCampaignProvider({ children }) {
  const [campaigns, setCampaigns] = useState([]);
  const [activeCampaignId, setActiveCampaignIdState] = useState(
    () => localStorage.getItem(STORAGE_KEY) || ""
  );
  const [loading, setLoading] = useState(true);

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/campaigns");
      setCampaigns(data);
      // If no active campaign, pick latest by created_at (already sorted desc)
      const savedId = localStorage.getItem(STORAGE_KEY);
      const stillExists = data.some((c) => c.id === savedId);
      if (!stillExists && data.length) {
        const next = data[0].id;
        localStorage.setItem(STORAGE_KEY, next);
        setActiveCampaignIdState(next);
      } else if (!data.length) {
        localStorage.removeItem(STORAGE_KEY);
        setActiveCampaignIdState("");
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);

  const setActiveCampaignId = useCallback((id) => {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
    setActiveCampaignIdState(id || "");
  }, []);

  const activeCampaign = campaigns.find((c) => c.id === activeCampaignId) || null;

  return (
    <ActiveCampaignCtx.Provider
      value={{
        campaigns,
        activeCampaignId,
        activeCampaign,
        setActiveCampaignId,
        refreshCampaigns: loadCampaigns,
        loading,
      }}
    >
      {children}
    </ActiveCampaignCtx.Provider>
  );
}

export function useActiveCampaign() {
  return useContext(ActiveCampaignCtx);
}
